import {
  Action_Type,
  Agent_AssignPriority,
  License_RegistrationType,
  Scalars,
  License_RegisterAgentRequest,
  License_RegisterAgentAction,
  Cluster_Nodes_AddInput,
  Cluster_Nodes_RemoveInput,
} from '@/graphql/codegen/generated/graphql';
import { useDialogPresets } from '@/services/dialogPresets.composable';
import { CONST_NOTIFY } from '@/services/notify/CONST';
import { useAgentsActions } from '@/views/agents/agents.helpers';
import { inject, Ref, VNodeRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { useAgentActionsMattomoMiddleware } from '@/services/matomo/matomo.middlewareActions';
import { BuildPriorityEnums, LogLevelEnums, SelectedAgent } from './AgentActionMenu.types';
import { useNotifyPresets } from './services/notifyPresets';
import { ResponseStatus } from '../../const/globals';

export const useAgentsCommonActions = (ref?: Ref<VNodeRef | undefined>) => {
  const PLEASE_HOLD_DIALOG_TIMEOUT = 2000;
  const { agentActionsMattomoMiddleware } = useAgentActionsMattomoMiddleware();

  const { setAgentsAction, setAgentLicense } = useAgentsActions();
  const { notifyPresets } = useNotifyPresets();
  const { dialogPresets, dialogState, closeDialog } = useDialogPresets();
  const { t } = useI18n();
  const coordinatorsService = inject(IKCoordinatorsService);

  const asyncActionWithPleaseHoldDialog = async <T>(asyncAction: T extends Promise<unknown> ? T : never, options?: { showDialog?: boolean }): Promise<T> => {
    /* Checking if the showDialog is false, if it is, it will return the asyncAction. */
    if (options?.showDialog === false) return asyncAction;
    let isFetching = true;
    try {
      setTimeout(() => {
        if (isFetching) {
          /* Showing a dialog with the title "Please hold" if the async action takes more than 2 seconds. */
          dialogPresets.value.pleaseHold();
        }
      }, PLEASE_HOLD_DIALOG_TIMEOUT);
      const result = await asyncAction;
      return result;
    } finally {
      isFetching = false;
      if (dialogState.value?.data?.dialogTitle?.content === t('agents.dialogs.pleaseHold.title')) closeDialog();
    }
  };

  const agentAction = async (type: Action_Type, agents: SelectedAgent[], value?: any) => {
    if (ref?.value) (ref?.value as any).hide();
    const results = await setAgentsAction(type, {
      agents: agents.map(agent => ({
        agentId: agent.id as string,
        coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
      })),
      parameter: value,
    });
    return results.data.agents_action_asyncResult?.result;
  };

  const setAgentDescription = async (agents: SelectedAgent[], value: string) => {
    const isBulk = agents.length > 1;
    try {
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.SetHostComment, agents, value), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentSetAgentDescriptionSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => (result?.status === ResponseStatus.Ok ? notifyPresets.agentSetAgentDescriptionSuccess() : notifyPresets.agentSetAgentDescriptionFail()));
    } catch (error) {
      notifyPresets.agentSetAgentDescriptionFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select submit Agent Description',
        bulk: isBulk,
      });
    }
  };

  const setBuildGroup = async (agents: SelectedAgent[], buildGroup: string) => {
    const isBulk = agents.length > 1;
    try {
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.SetBuildGroup, agents, buildGroup), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentsActionSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentsActionSuccess();
        return notifyPresets.agentSingeActionFail();
      });
    } catch (error) {
      notifyPresets.agentSingeActionFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select build group',
        name: buildGroup,
        bulk: isBulk,
      });
    }
  };

  const setRemoveAgentLicense = async (agents: SelectedAgent[], actionType: License_RegisterAgentAction) => {
    const isBulk = agents.length > 1;
    try {
      if (ref?.value) (ref?.value as any).hide();

      const params = agents.map(agent => ({
        requestedCores: 0,
        agentId: agent.id as string,
        initiatorLicense: License_RegistrationType.None,
        ciInitiatorLicense: License_RegistrationType.None,
        helperLicense: License_RegistrationType.None,
        action: actionType,
        coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
      }));
      const results = await asyncActionWithPleaseHoldDialog(setAgentLicense(params), { showDialog: isBulk });
      if (isBulk) {
        const updatedAgents = results?.data?.license_registerAgents_asyncResult?.result?.filter(res => res?.status === ResponseStatus.Ok).length || 0;
        return results?.data?.license_registerAgents_asyncResult?.result?.length === updatedAgents
          ? notifyPresets.agentsActionSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.data?.license_registerAgents_asyncResult?.result?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.data?.license_registerAgents_asyncResult?.result?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentsActionSuccess();
        return notifyPresets.agentSingeActionFail();
      });
    } catch (error) {
      notifyPresets.agentSingeActionFail();
    } finally {
      if (actionType === License_RegisterAgentAction.UnsubscribeAgent) {
        agentActionsMattomoMiddleware({
          action: 'Select unsubscribe agent',
          bulk: isBulk,
        });
      }
    }
  };

  const setReleaseCloudAgent = (agents: SelectedAgent[]) => {
    dialogPresets.value.releaseAgentPopupDialog(
      async () => {
        try {
          const isBulk = agents?.length > 1;
          const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.ReleaseCloud, agents), { showDialog: isBulk });
          if (isBulk) {
            const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
            return results?.length === updatedAgents
              ? notifyPresets.agentsActionSuccess(updatedAgents)
              : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
                  results?.map(result => ({
                    status: result?.status || '',
                    agentName: result?.agentName || '',
                    message: result?.message || '',
                    errorCode: result?.errorCode || null,
                  })) || [],
                  updatedAgents,
                );
          }
          results?.forEach(result => (result?.status === ResponseStatus.Ok ? notifyPresets.agentsActionSuccess() : notifyPresets.agentSingeActionFail()));
        } catch (error) {
          notifyPresets.agentSingeActionFail();
        }
      },
      () => {},
    );
  };

  const setAgentsLicense = async (data: License_RegisterAgentRequest[]) => {
    const isBulk = data.length > 1;
    try {
      if (ref?.value) (ref?.value as any).hide();
      const results = await asyncActionWithPleaseHoldDialog(setAgentLicense(data), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.data?.license_registerAgents_asyncResult?.result?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        const resultsLength = results?.data?.license_registerAgents_asyncResult?.result?.length || 0;
        const numberOfIssues = resultsLength - updatedAgents;
        if (resultsLength === updatedAgents) {
          notifyPresets.agentsActionSuccess(updatedAgents);
        } else {
          dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
            results?.data?.license_registerAgents_asyncResult?.result?.map(result => ({
              status: result?.status || '',
              agentName: result?.agentName || '',
              message: result?.message || '',
              errorCode: result?.errorCode || null,
            })) || [],
            (results?.data?.license_registerAgents_asyncResult?.result?.length || 0) - numberOfIssues,
          );
        }
      } else {
        // single agent
        let messageType = CONST_NOTIFY.MESSAGE_TYPE.SUCCESS;
        let message = 'default'; // default message
        const agent = results?.data?.license_registerAgents_asyncResult?.result?.length ? results?.data?.license_registerAgents_asyncResult?.result[0] : {};
        if (agent?.status !== ResponseStatus.Ok) {
          messageType = CONST_NOTIFY.MESSAGE_TYPE.ERROR;
          const licenseRanOutStrings = [
            'No available fixed initiator licenses to register the initiator',
            'No available fixed cores to register the helper',
            'No available fixed CI initiator licenses to register the initiator.',
          ];
          if (licenseRanOutStrings.some(ranOutString => agent?.message?.includes(ranOutString))) message = 'licenseRanOut';
          // other errors result in default msg until documented.
        } else {
          // it means that the status is 'ok'
          const pendingQueueStrings = ['Not enough floating cores licenses'];
          if (pendingQueueStrings.some(pendingQueueString => agent?.message?.includes(pendingQueueString))) {
            messageType = CONST_NOTIFY.MESSAGE_TYPE.WARNING;
            message = 'pendingQueue';
          }
        }
        notifyPresets.agentSingleLicenseConfigUpdate(messageType, message);
      }
    } catch (error) {
      notifyPresets.agentSingeActionFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select submit License configurations',
        bulk: isBulk,
      });
    }
  };

  const setUpdateVersion = async (agents: SelectedAgent[]) => {
    try {
      const isBulk = agents.length > 1;
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.AgentUpdateVersion, agents), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentsActionSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => (result?.status === ResponseStatus.Ok ? notifyPresets.agentsActionSuccess() : notifyPresets.agentSingeActionFail()));
    } catch (error) {
      notifyPresets.agentSingeActionFail();
    }
  };

  return {
    setBuildGroup,
    setRemoveAgentLicense,
    setReleaseCloudAgent,
    asyncActionWithPleaseHoldDialog,
    agentAction,
    setUpdateVersion,
    setAgentsLicense,
    setAgentDescription,
  };
};

const useAgentsWindowsActions = (ref?: Ref<VNodeRef | undefined>) => {
  const { agentActionsMattomoMiddleware } = useAgentActionsMattomoMiddleware();
  const coordinatorsService = inject(IKCoordinatorsService);
  const { notifyPresets } = useNotifyPresets();
  const { dialogPresets } = useDialogPresets();
  const { setBuildCacheSettings, setAllocateBuildCache, setAddBuildCacheClusterNodes, setRemoveBuildCacheClusterNodes } = useAgentsActions();
  const { asyncActionWithPleaseHoldDialog, agentAction } = useAgentsCommonActions(ref);

  const setBuildCacheSizeGB = async (data: { agents: SelectedAgent[]; cacheSizeGB: Scalars['Int'] }) => {
    const isBulk = data.agents.length > 1;
    try {
      if (ref?.value) (ref?.value as any).hide();
      const results = await asyncActionWithPleaseHoldDialog(
        setBuildCacheSettings({ ...data, agents: data.agents.map(agent => ({ agentId: agent.id as string, coordinatorId: coordinatorsService?.selectedCoordinator.value || '' })) }),
        { showDialog: isBulk },
      );

      if (isBulk) {
        const updatedAgents = results.data.agents_buildCache_update_asyncResult?.result?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results.data.agents_buildCache_update_asyncResult?.result?.length === updatedAgents
          ? notifyPresets.agentsActionSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.data?.agents_buildCache_update_asyncResult?.result?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results.data.agents_buildCache_update_asyncResult?.result?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentsActionSuccess();
        return notifyPresets.agentSingeActionFail();
      });
    } catch (error) {
      notifyPresets.agentSingeActionFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select submit cache size',
        bulk: isBulk,
      });
    }
  };

  const setBuildCacheFolderLocation = async (data: { agents: SelectedAgent[]; cacheFolder: Scalars['String']; databaseFolder: Scalars['String'] }) => {
    const isBulk = data.agents.length > 1;
    try {
      if (ref?.value) (ref?.value as any).hide();
      const results = await asyncActionWithPleaseHoldDialog(
        setBuildCacheSettings({ ...data, agents: data.agents.map(agent => ({ agentId: agent.id as string, coordinatorId: coordinatorsService?.selectedCoordinator.value || '' })) }),
        { showDialog: isBulk },
      );

      if (isBulk) {
        const updatedAgents = results.data.agents_buildCache_update_asyncResult?.result?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results.data.agents_buildCache_update_asyncResult?.result?.length === updatedAgents
          ? notifyPresets.agentsActionSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results.data.agents_buildCache_update_asyncResult?.result?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results.data.agents_buildCache_update_asyncResult?.result?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentsActionSuccess();
        return notifyPresets.agentSingeActionFail();
      });
    } catch (error) {
      notifyPresets.agentSingeActionFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select submit Build Cache Folder Location',
        bulk: isBulk,
      });
    }
  };

  const setReizeCache = async (agents: SelectedAgent[], value: number) => {
    const isBulk = agents.length > 1;
    try {
      if (ref?.value) (ref?.value as any).hide();
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.ResizeAgentCache, agents, value), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentResizeCacheSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentResizeCacheSuccess();
        return notifyPresets.agentResizeCacheFail();
      });
    } catch (error) {
      notifyPresets.agentResizeCacheFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select submit cache size',
        bulk: isBulk,
      });
    }
  };

  const setLogLevel = async (agents: SelectedAgent[], value: LogLevelEnums) => {
    const isBulk = agents.length > 1;
    try {
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.SetAgentLoggingLevel, agents, value), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentLogLevelSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentLogLevelSuccess();
        return notifyPresets.agentLogLevelFail();
      });
    } catch (error) {
      notifyPresets.agentLogLevelFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select Log level',
        bulk: isBulk,
      });
    }
  };

  const setBuildPriority = async (agents: SelectedAgent[], value: BuildPriorityEnums) => {
    const isBulk = agents.length > 1;
    try {
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.SetBuildPriority, agents, value), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentBuildPrioritySuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentBuildPrioritySuccess();
        return notifyPresets.agentBuildPriorityFail();
      });
    } catch (error) {
      notifyPresets.agentBuildPriorityFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select Build priority',
        bulk: isBulk,
      });
    }
  };

  const setAssignmentPriority = async (agents: SelectedAgent[], value: Agent_AssignPriority) => {
    const isBulk = agents.length > 1;
    try {
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.SetAgentAssignPriority, agents, value), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentAssignmentPrioritySuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentAssignmentPrioritySuccess();
        return notifyPresets.agentAssignmentPriorityFail();
      });
    } catch (error) {
      notifyPresets.agentAssignmentPriorityFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select assignment priority',
        bulk: isBulk,
      });
    }
  };

  const setClearCache = async (agents: SelectedAgent[]) => {
    const isBulk = agents.length > 1;
    try {
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.AgentClearCache, agents), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentClearCacheSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => (result?.status === ResponseStatus.Ok ? notifyPresets.agentClearCacheSuccess() : notifyPresets.agentClearCacheFail()));
    } catch (error) {
      notifyPresets.agentClearCacheFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select clean helper cache',
        bulk: isBulk,
      });
    }
  };

  const setAllowHelperEnabled = async (agents: SelectedAgent[], value: boolean) => {
    const isBulk = agents.length > 1;
    try {
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.AllowEnableAgent, agents, value), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentSetAllowHelperEnabledSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => (result?.status === ResponseStatus.Ok ? notifyPresets.agentSetAllowHelperEnabledSuccess() : notifyPresets.agentSetAllowHelperEnabledFail()));
    } catch (error) {
      notifyPresets.agentSetAllowHelperEnabledFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: `Select/Unselect Allow to 'enable/disable as helper'`,
        bulk: isBulk,
        value: Number(value),
      });
    }
  };

  const setHelperEnabled = async (agents: SelectedAgent[], value: boolean) => {
    const isBulk = agents.length > 1;
    try {
      const results = await asyncActionWithPleaseHoldDialog(agentAction(Action_Type.EnableAgent, agents, value), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentSetHelperEnabledSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => (result?.status === ResponseStatus.Ok ? notifyPresets.agentSetHelperEnabledSuccess() : notifyPresets.agentSetHelperEnabledFail()));
    } catch (error) {
      notifyPresets.agentSetHelperEnabledFail();
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select/Unselect Enabled as Helper',
        bulk: isBulk,
        value: Number(value),
      });
    }
  };

  const setLicenseBuildAvoidance = async (agentsPayload: any) => {
    const allocateTypeValue = agentsPayload[0].allocateBuildCache ? 'enabled' : 'disabled';
    const isBulk = agentsPayload.length > 1;
    try {
      if (ref?.value) (ref?.value as any).hide();
      const results = await asyncActionWithPleaseHoldDialog(
        setAllocateBuildCache(
          agentsPayload.map((agent: any) => ({
            agentId: agent.agentId as string,
            coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
            enable: agent.allocateBuildCache,
            useLocalCache: agent.useLocalCache,
            remoteCacheServer: agent.remoteCacheServer,
            useBuildCacheCluster: agent.useBuildCacheCluster,
          })),
        ),
        { showDialog: isBulk },
      );

      if (isBulk) {
        const updatedAgents = results?.data.license_registerBuildCache_asyncResult?.result?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.data.license_registerBuildCache_asyncResult?.result?.length === updatedAgents
          ? notifyPresets.agentBuildAvoidanceSuccess(allocateTypeValue, updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.data.license_registerBuildCache_asyncResult?.result?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.data?.license_registerBuildCache_asyncResult?.result?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentBuildAvoidanceSuccess(allocateTypeValue);
        return notifyPresets.agentBuildAvoidanceFail(allocateTypeValue);
      });
    } catch (error) {
      notifyPresets.agentBuildAvoidanceFail(allocateTypeValue);
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Select/Unselect Allocate Build Cache',
        bulk: isBulk,
        value: Number(allocateTypeValue),
      });
    }
  };

  const setBuildCacheClusterUpdateNodes = async (agents: SelectedAgent[], clusterId: number) => {
    const isBulk = agents.length > 1;
    try {
      if (ref?.value) (ref?.value as any).hide();
      if (clusterId === -1) {
        const results = await setRemoveBuildCacheClusterNodes({ agents: agents.map(agent => agent.id) } as Cluster_Nodes_RemoveInput);
        if (results?.data?.cluster_remove_nodes?.status === ResponseStatus.Ok) {
          return notifyPresets.agentsActionSuccess(agents.length);
        }
      } else {
        const data = { agents: agents.map(agent => agent.id), clusterId } as Cluster_Nodes_AddInput;
        const results = await setAddBuildCacheClusterNodes(data);
        if (isBulk) {
          const updatedAgents = results?.data?.cluster_add_nodes?.result?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
          return results?.data?.cluster_add_nodes?.result?.length === updatedAgents
            ? notifyPresets.agentsActionSuccess(updatedAgents)
            : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
                results?.data?.cluster_add_nodes?.result?.map(result => ({
                  status: result?.status || '',
                  agentName: result?.agentName || '',
                  message: '',
                  errorCode: null,
                })) || [],
                updatedAgents,
              );
        }
        results?.data?.cluster_add_nodes?.result?.forEach(result => {
          if (result?.status === ResponseStatus.Ok) {
            return notifyPresets.agentsActionSuccess();
          }
          return notifyPresets.agentSingeActionFail();
        });
      }
    } catch (error) {
      return notifyPresets.agentSingeActionFail();
    }
  };

  // buildGroup
  // licenseConfiguration

  return {
    setReizeCache,
    setLicenseBuildAvoidance,
    setHelperEnabled,
    setAllowHelperEnabled,
    setClearCache,
    setAssignmentPriority,
    setBuildPriority,
    setLogLevel,
    setBuildCacheFolderLocation,
    setBuildCacheSizeGB,
    setBuildCacheClusterUpdateNodes,
  };
};
const useAgentsLinuxActions = (ref?: Ref<VNodeRef | undefined>) => {
  const commonActions = useAgentsCommonActions(ref);
  const { notifyPresets } = useNotifyPresets();
  const { dialogPresets } = useDialogPresets();
  const { agentAction } = useAgentsCommonActions(ref);
  const removeAgentLinux = async (agents: SelectedAgent[]) => {
    try {
      const isBulk = agents.length > 1;
      const results = await commonActions.asyncActionWithPleaseHoldDialog(agentAction(Action_Type.RemoveAgent, agents), { showDialog: isBulk });

      if (isBulk) {
        const updatedAgents = results?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
        return results?.length === updatedAgents
          ? notifyPresets.agentsActionSuccess(updatedAgents)
          : dialogPresets.value.agentsBulkActionPartialSuccessOrFail(
              results?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              updatedAgents,
            );
      }
      results?.forEach(result => {
        if (result?.status === ResponseStatus.Ok) return notifyPresets.agentsActionSuccess();
        return notifyPresets.agentSingeActionFail();
      });
    } catch (error) {
      notifyPresets.agentSingeActionFail();
    }
  };

  return {
    removeAgentLinux,
  };
};

type AgentActionsMenuResult = {
  [OsType.Windows]: ReturnType<typeof useAgentsCommonActions> & ReturnType<typeof useAgentsWindowsActions>;
  [OsType.Linux]: ReturnType<typeof useAgentsCommonActions> & ReturnType<typeof useAgentsLinuxActions>;
  [OsType.Mac]: ReturnType<typeof useAgentsCommonActions> & ReturnType<typeof useAgentsLinuxActions>;
};
export const useAgentActionsMenu = <T extends OsType>(os: T, ref?: Ref<VNodeRef | undefined>): AgentActionsMenuResult[T] => {
  if (os === OsType.Windows) {
    const windowsActions = useAgentsWindowsActions(ref);
    const common = useAgentsCommonActions(ref);
    const merge = { ...windowsActions, ...common };
    return merge as AgentActionsMenuResult[T];
  }
  const linuxActions = useAgentsLinuxActions(ref);
  const common = useAgentsCommonActions(ref);
  const merge = { ...linuxActions, ...common };
  return merge as AgentActionsMenuResult[T];
};
